import { Link } from 'react-router-dom';
import { ArrowUpRight, Users, Sparkles, Globe2, Trophy } from 'lucide-react';
import { FadeUp, TextReveal, Counter } from '@/components/site/Reveal';
import { STATIC } from '@/lib/cms';
import SEO from '@/components/site/SEO';

export default function About() {
  return (
    <>
      <SEO title="About — Slinkflow" description="About Slinkflow — our story, team, and approach to design and engineering." />
      {/* Hero */}
      <section className="relative overflow-hidden pt-40 pb-24 lg:pt-56 lg:pb-22">
        <div className="absolute -top-32 right-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">About Slinkflow</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] md:text-[88px] lg:text-[112px]">
            <TextReveal text="A studio for ambitious" />
            <br />
            <span className="gradient-text font-medium"><TextReveal text="founders & operators." delay={0.2} /></span>
          </h1>
          <FadeUp delay={0.5} className="mt-12 grid gap-10 lg:grid-cols-12 lg:pt-10">
            <p className="lg:col-span-7 text-balance text-[20px] leading-relaxed text-ink-500 flex items-center lg:text-start text-center ">
              We&apos;re a senior team of designers, engineers and strategists who&apos;ve spent careers shipping at the top of the industry. We came together to build the agency we always wanted to hire: small, opinionated, ridiculously good at the craft, and honest about outcomes.
            </p>
            <div className="lg:col-span-5 grid grid-cols-2 gap-6">
              {STATIC.stats.slice(0, 4).map((s, i) => (
                <div key={i} className="rounded-3xl border border-ink-100 bg-white p-6">
                  <p className="font-display text-3xl font-light text-ink-900"><Counter to={s.value} suffix={s.suffix} /></p>
                  <p className="mt-2 text-sm text-ink-500">{s.label}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-9 lg:py-12">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-6 md:grid-cols-3 md:px-10">
          {[
            { icon: Sparkles, t: 'Mission', d: 'Help ambitious brands convert attention into revenue through premium digital experiences.' },
            { icon: Globe2, t: 'Vision', d: 'A web where every interaction feels intentional, fast and genuinely delightful.' },
            { icon: Trophy, t: 'Values', d: 'Craft over churn. Outcomes over deliverables. Senior over scaled. Honest over polite.' },
          ].map((it, i) => (
            <FadeUp key={it.t} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-ink-100 bg-white p-10">
                <it.icon className="h-8 w-8 text-cyan" strokeWidth={1.5} />
                <h3 className="mt-8 font-display text-3xl text-ink-900">{it.t}</h3>
                <p className="mt-3 text-[16px] leading-relaxed text-ink-500">{it.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-ink-50 py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Our story</p>
          <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] md:text-[60px]">From <span className="gradient-text font-medium">first sketch</span> to today.</h2>

          <div className="mt-16 space-y-12">
            {[
              { year: '2019', t: 'Founded in San Francisco', d: 'Three friends from Stripe, Linear and IDEO opened a tiny studio above a bookstore.' },
              { year: '2021', t: '50+ premium projects shipped', d: 'Awwwards SOTD wins. First million-dollar quarter. Hired our first 5 senior contributors.' },
              { year: '2023', t: 'Global footprint', d: 'Clients across 27 countries. Opened a remote-first hub for European timezones.' },
              { year: '2026', t: 'A studio you can trust', d: 'Selective, senior, and obsessed with the metrics our clients actually care about.' },
            ].map((it, i) => (
              <FadeUp key={it.year} delay={i * 0.05}>
                <div className="grid items-start gap-6 border-t border-ink-200 pt-10 md:grid-cols-12">
                  <p className="md:col-span-2 font-display text-3xl text-cyan">{it.year}</p>
                  <p className="md:col-span-4 font-display text-2xl text-ink-900">{it.t}</p>
                  <p className="md:col-span-6 text-[16px] leading-relaxed text-ink-500">{it.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {/* <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-cyan">The team</p>
              <h2 className="mt-4 font-display text-[44px] font-light leading-[1.05] md:text-[60px]">People behind <span className="gradient-text font-medium">the work.</span></h2>
            </div>
            <p className="max-w-md text-[16px] text-ink-500">A focused bench of designers, engineers and strategists building your next launch.</p>
          </div>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {STATIC.team.map((m, i) => (
              <FadeUp key={m.name} delay={i * 0.04}>
                <div className="group overflow-hidden rounded-3xl bg-ink-50">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={m.avatar} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <p className="font-display text-lg text-ink-900">{m.name}</p>
                    <p className="mt-1 text-xs text-ink-500">{m.role}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section> */}

      <section className="bg-ink-900 py-24 text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-8 px-6 md:flex-row md:items-center md:justify-between md:px-10">
          <h3 className="font-display text-[36px] font-light leading-tight md:text-[52px]">Want to work with us?</h3>
          <Link to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-5 text-ink-900">
            Start a project <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </div>
      </section>
    </>
  );
}
